'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { FadeIn, SlideUp } from '@/components/ui/motion';

interface FeaturedPet {
  id: string;
  name: string;
  species: string;
  breed: string;
  age: string;
  image: string;
  alt: string;
  story: string;
}

interface HeroSectionProps {
  featuredPets: FeaturedPet[];
}

const HeroSection = ({ featuredPets }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentPetIndex, setCurrentPetIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentPetIndex((prev) => (prev + 1) % featuredPets.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, featuredPets.length]);

  const handleNext = () => {
    setCurrentPetIndex((prev) => (prev + 1) % featuredPets.length);
  };

  const handlePrev = () => {
    setCurrentPetIndex((prev) => (prev - 1 + featuredPets.length) % featuredPets.length);
  };

  const getVisiblePets = () => {
    if (!featuredPets.length) return [];
    const pets = [];
    for (let i = 0; i < Math.min(featuredPets.length, 3); i++) {
      pets.push(featuredPets[(currentPetIndex + i) % featuredPets.length]);
    }
    return pets;
  };

  const visiblePets = getVisiblePets();

  return (
    <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 overflow-hidden min-h-[90vh] flex items-center">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-16 lg:py-24 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 lg:space-y-8 z-10">
            <FadeIn delay={0.2}>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full animate-fade-in">
                <Icon name="HeartIcon" size={20} variant="solid" className="text-accent" />
                <span className="text-sm font-medium text-accent">Every Pet Deserves a Perfect Match</span>
              </div>
            </FadeIn>

            <SlideUp delay={0.3}>
              <h1 className="text-4xl lg:text-6xl font-headline font-semibold text-foreground leading-tight">
                Your Journey to Responsible Pet Parentship Starts Here
              </h1>
            </SlideUp>

            <SlideUp delay={0.4}>
              <p className="text-lg lg:text-xl text-muted-foreground font-body max-w-lg">
                Love guided by wisdom. We bridge the gap between homeless pets and loving families while providing the expertise you need for lifelong companionship.
              </p>
            </SlideUp>

            <SlideUp delay={0.5}>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  href="/pet-discovery"
                  className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-brand-cta text-white font-headline font-semibold rounded-lg hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Icon name="MagnifyingGlassIcon" size={20} variant="outline" />
                  <span>Find Your Companion</span>
                </Link>

                <Link
                  href="/care-guides-hub"
                  className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-card text-foreground font-headline font-semibold rounded-lg border-2 border-primary hover:bg-primary hover:text-white transition-all duration-300"
                >
                  <Icon name="BookOpenIcon" size={20} variant="outline" />
                  <span>Explore Care Guides</span>
                </Link>
              </div>
            </SlideUp>

            <FadeIn delay={0.6}>
              <div className="flex items-center space-x-8 pt-4">
                <div className="text-center">
                  <div className="text-3xl font-headline font-semibold text-primary">12,500+</div>
                  <div className="text-sm text-muted-foreground">Successful Adoptions</div>
                </div>
                <div className="w-px h-12 bg-border"></div>
                <div className="text-center">
                  <div className="text-3xl font-headline font-semibold text-primary">850+</div>
                  <div className="text-sm text-muted-foreground">Shelter Partners</div>
                </div>
                <div className="w-px h-12 bg-border"></div>
                <div className="text-center">
                  <div className="text-3xl font-headline font-semibold text-primary">98%</div>
                  <div className="text-sm text-muted-foreground">Success Rate</div>
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="relative h-[600px] w-full flex items-center justify-center perspective-[1000px]">
            {/* Background decorative elements for the stack */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[80%] h-[80%] bg-primary/5 rounded-full blur-3xl absolute -translate-y-12 translate-x-12"></div>
            </div>

            <div className="relative w-[340px] h-[500px]">
              {isHydrated && visiblePets.reverse().map((pet, index) => {
                // Adjust index because we reversed the array to render back-to-front
                const originalIndex = visiblePets.length - 1 - index;
                const isFirst = originalIndex === 0;
                return (
                  <div
                    key={`${pet.id}-${originalIndex}`}
                    onClick={() => isFirst && handleNext()}
                    className={`absolute inset-0 rounded-3xl overflow-hidden shadow-2xl transition-all duration-500 ease-out cursor-pointer bg-white border border-white/20
                      ${isFirst ? 'z-30 scale-100 opacity-100 translate-y-0 shadow-brand-cta/20' : ''}
                      ${originalIndex === 1 ? 'z-20 scale-95 opacity-80 translate-y-6 translate-x-6' : ''}
                      ${originalIndex === 2 ? 'z-10 scale-90 opacity-60 translate-y-12 translate-x-12' : ''}
                      ${originalIndex > 2 ? 'opacity-0' : ''}
                    `}
                    style={{
                      transformOrigin: 'bottom center'
                    }}
                  >
                    <AppImage
                      src={pet.image}
                      alt={pet.alt}
                      className="w-full h-full object-cover"
                    />

                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-6 text-white pt-24">
                      <div className="transform transition-transform duration-300 translate-y-0 group-hover:-translate-y-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="px-2.5 py-0.5 bg-primary/90 text-white rounded-full text-xs font-bold shadow-sm backdrop-blur-sm">
                            {pet.species}
                          </span>
                          <span className="px-2.5 py-0.5 bg-white/20 text-white rounded-full text-xs font-medium backdrop-blur-sm">
                            {pet.age}
                          </span>
                        </div>
                        <h3 className="text-2xl font-bold mb-1">{pet.name}</h3>
                        <p className="text-white/80 text-sm line-clamp-2">{pet.story}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Navigation controls */}
            <div className="absolute bottom-12 right-0 lg:right-12 flex space-x-3 z-40">
              <button
                onClick={handlePrev}
                className="p-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-foreground hover:bg-white hover:text-primary transition-all shadow-lg"
                aria-label="Previous pet"
              >
                <Icon name="ChevronLeftIcon" size={24} />
              </button>
              <button
                onClick={handleNext}
                className="p-3 rounded-full bg-primary text-white shadow-lg hover:bg-primary/90 hover:scale-105 transition-all"
                aria-label="Next pet"
              >
                <Icon name="ChevronRightIcon" size={24} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;